import { mapWebData, type TrackingIncident, type TrackingPath, type TrackingSpeedEvent, type TrackingStop, type Vehicle } from '@cobliteam/api-lib';
import {
  DEFAULT_FUEL_CONSUMPTION,
  DEFAULT_GAS_PRICE,
  LITERS_PER_IDLE_SECOND,
  type StartEndDateRange,
  toFormattedComsumptionInLiters,
  toFormattedStringMoney,
} from '@cobliteam/common-types';
import moment from 'moment';
import { compose } from 'redux';
import { createSelector } from 'reselect';
import { selectIncidentsWebData, selectPathGeojson, selectRoadSpeedEvents, selectStops, selectTrackingDomain, selectVehicleSpeedEvents } from './common';
import type { FuelConsumption, ProcessedStop, StartEndIndexes } from './Types';

export const selectSelectedVehicleId = compose((state) => state.selectedVehicleId, selectTrackingDomain);

export const selectSelectedTriggerData = compose((state) => state.selectedTrigger, selectTrackingDomain);

export const selectSelectedGeofenceId = compose((state) => state.selectedGeofenceId, selectTrackingDomain);

export const selectSavedRoutesWebData = compose((state) => state.savedRoutesWebData, selectTrackingDomain);

export const selectTrackPeriodFilter = compose((state) => state.selectedTripTime, selectTrackingDomain);

export const selectTrackingTabOpen = compose((state) => state.trackingTabOpen, selectTrackingDomain);

export const selectSelectedDeviceId = compose((state) => state.selectedDeviceId, selectTrackingDomain);

export const getTrackWholePeriod = (pathGeojson: TrackingPath, trackingStops: TrackingStop[], trackingIncident: TrackingIncident[]): StartEndDateRange => {
  const { timestamps } = pathGeojson.properties;

  if (!timestamps.length) return null;

  const lastIncidentTime = trackingIncident.length ? trackingIncident[trackingIncident.length - 1].time : undefined;

  const lastStop = trackingStops?.length ? trackingStops[trackingStops.length - 1] : undefined;
  const lastStopStart = lastStop?.startTime;
  const pathGeoJsonEnd = timestamps.length && timestamps[timestamps.length - 1];

  let trackingEnd = pathGeoJsonEnd;

  if (lastStopStart && lastStopStart > trackingEnd) {
    trackingEnd = lastStopStart;
  }

  if (lastIncidentTime && lastIncidentTime > trackingEnd) {
    trackingEnd = lastIncidentTime;
  }

  return { start: timestamps.length && timestamps[0], end: trackingEnd };
};

/**
 * Extracts start and end timestamps from a given route/path
 * @method selectTrackWholePeriod
 * @param  state: AppState
 * @return {object}  Start and End dates
 */
export const selectTrackWholePeriod = createSelector(selectPathGeojson, selectStops, selectIncidentsWebData, mapWebData(getTrackWholePeriod));

export const getStartEndIndexes = (pathGeojson: TrackingPath, tripTime: StartEndDateRange): StartEndIndexes => {
  let startIndex = 0;
  let endIndex = 0;

  const timestamps = pathGeojson.properties.timestamps.sort();

  if (tripTime && timestamps.length) {
    endIndex = timestamps.length - 1;

    const { start, end } = tripTime;
    if (timestamps[0] !== start) {
      for (let i = 1; i < timestamps.length; i++) {
        const timestampBefore = timestamps[i - 1];
        const timestampCurrent = timestamps[i];

        if (start > timestampBefore && start <= timestampCurrent) {
          startIndex = i;
          break;
        }
      }
    }

    if (timestamps[endIndex] !== end) {
      for (let i = 1; i < timestamps.length; i++) {
        const timestampBefore = timestamps[i - 1];
        const timestampCurrent = timestamps[i];

        if (timestampCurrent > end && timestampBefore <= end) {
          endIndex = i - 1;
          break;
        }
      }
    }
  }

  // TODO: this should be null, not (0,0). When pathGeojson is empty we shouldn't pass around indexes === 0.
  return { startIndex, endIndex };
};

export const getFilteredTrackPeriod = (trackPeriod: StartEndDateRange, trackPeriodFilter: StartEndDateRange) => {
  return trackPeriodFilter === null ? trackPeriod : trackPeriodFilter;
};

export const selectVisibleTrackPeriod = createSelector(selectTrackWholePeriod, selectTrackPeriodFilter, mapWebData(getFilteredTrackPeriod));

const selectStartEndIndexes = createSelector(selectPathGeojson, selectVisibleTrackPeriod, mapWebData(getStartEndIndexes));

export const filterPathGeojson = (pathGeojson: TrackingPath, startEndIndexes: StartEndIndexes): TrackingPath => {
  const { startIndex, endIndex } = startEndIndexes;

  const emptyPath = {
    ...pathGeojson,
    geometry: {
      ...pathGeojson.geometry,
      coordinates: [],
    },
    properties: {
      timestamps: [],
    },
  };

  if (pathGeojson.geometry.coordinates.length === 0) {
    return emptyPath;
  }

  const validStartIndex = startIndex < 0 ? 0 : startIndex;
  const validEndIndex = endIndex >= pathGeojson.geometry.coordinates.length ? pathGeojson.geometry.coordinates.length - 1 : endIndex;

  const filteredTimestamp = pathGeojson.properties.timestamps.slice(validStartIndex, validEndIndex + 1);
  const filteredCoordinates = pathGeojson.geometry.coordinates.slice(validStartIndex, validEndIndex + 1);

  if (filteredTimestamp.length === 0 || filteredCoordinates.length === 0) {
    return emptyPath;
  }

  return {
    type: pathGeojson.type,
    geometry: {
      type: pathGeojson.geometry.type,
      coordinates: filteredCoordinates,
    },
    properties: {
      timestamps: filteredTimestamp,
    },
  };
};

export const selectFilteredPathGeojson = createSelector(selectPathGeojson, selectStartEndIndexes, mapWebData(filterPathGeojson));

const mapToProcessedStop = (trackingStop: TrackingStop, idx: number): ProcessedStop => {
  return {
    ...trackingStop,
    index: idx,
  };
};

export const filterAndProcessStops = (stops: TrackingStop[], selectedTripTime: StartEndDateRange): ProcessedStop[] => {
  if (!selectedTripTime) {
    return [];
  }

  const isNotFromPreviousDay = (stop: TrackingStop): boolean => {
    const { startTime } = stop;

    const dayOfStopStart = moment(startTime).startOf('day');
    const dayOfTripTime = moment(selectedTripTime.start).startOf('day');

    return dayOfStopStart.valueOf() === dayOfTripTime.valueOf();
  };

  const isInsidePeriod = (stop: ProcessedStop): boolean => {
    const { startTime, endTime } = stop;
    const duration = endTime - startTime;

    return startTime + duration >= selectedTripTime.start && startTime <= selectedTripTime.end;
  };

  return stops
    .filter(isNotFromPreviousDay)
    .map((stop, idx) => mapToProcessedStop(stop, idx))
    .filter(isInsidePeriod);
};

export const selectFilterAndProcessStops = createSelector(selectStops, selectVisibleTrackPeriod, mapWebData(filterAndProcessStops));

export const calculateFuelConsumption = (vehicle: Vehicle, distanceDrivenInMeters: number, idleTimeInMillis: number): FuelConsumption => {
  const fuelConsumption = vehicle.fuelConsumption || DEFAULT_FUEL_CONSUMPTION;
  const fuelPrice = vehicle.fuelPrice || DEFAULT_GAS_PRICE;

  const metersPerLiter = fuelConsumption * 1000;
  const idleTimeInSeconds = idleTimeInMillis / 1000;
  const consumedLiters = distanceDrivenInMeters / metersPerLiter + idleTimeInSeconds * LITERS_PER_IDLE_SECOND;

  return {
    liters: toFormattedComsumptionInLiters(consumedLiters, 0, 1),
    price: toFormattedStringMoney(consumedLiters * fuelPrice),
  };
};

export const filterSpeedEvents = (speedEvents: TrackingSpeedEvent[], period: StartEndDateRange | undefined): TrackingSpeedEvent[] => {
  return period ? speedEvents.filter((event) => event.eventTime >= period.start && event.eventTime <= period.end) : speedEvents;
};

export const selectFilteredRoadSpeedEvents = createSelector(selectRoadSpeedEvents, selectVisibleTrackPeriod, mapWebData(filterSpeedEvents));

export const selectFilteredVehicleSpeedEvents = createSelector(selectVehicleSpeedEvents, selectVisibleTrackPeriod, mapWebData(filterSpeedEvents));

export const filterIncidentsEvents = (incidents: TrackingIncident[], period: StartEndDateRange | undefined) =>
  period ? incidents.filter((event) => event.time >= period.start && event.time <= period.end) : incidents;

export const selectFilteredIncidentEvents = createSelector(selectIncidentsWebData, selectVisibleTrackPeriod, mapWebData(filterIncidentsEvents));
